import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets/icons/general/Close";
import { Button } from "@/components/ui/buttons/Button/Button";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Autocomplete } from "@/components/ui/inputs/Autocomplete/Autocomplete";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { BizJobTitleQueries } from "@/data/bizJobTitle/bizJobTitle.queries";
import { BizLocationQueries } from "@/data/bizLocation/bizLocation.queries";
import type { BizTagsModels } from "@/data/bizTags/bizTags.models";
import { BizTagsQueries } from "@/data/bizTags/bizTags.queries";
import type { CommonModels } from "@/data/common/common.models";

export interface IUploadsProfileFormData {
  jobPosition: string;
  location: string;
  tags: CommonModels.BizTagDto[];
}

interface IProps {
  jobTitle?: string;
  metroArea?: string;
  tags?: string[];
  children: React.ReactNode;
  subtitle: string;
  onConfirm: (data: IUploadsProfileFormData) => void;
  onChange?: (data: IUploadsProfileFormData) => void;
}

const UploadsProfileForm = ({ subtitle, children, onConfirm, onChange, jobTitle, metroArea, tags }: IProps) => {
  const [selectedJobPosition, setSelectedJobPosition] = useState<string>(jobTitle || "");
  const [location, setLocation] = useState<string>(metroArea || "");
  const [selectedTags, setSelectedTags] = useState<CommonModels.BizTagDto[]>([]);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync: createTag } = BizTagsQueries.useCreateTag();
  const { data: tagsList } = BizTagsQueries.useListTags({ limit: 50 });
  const [jobPositionSearch, setJobPositionSearch] = useState<string>();
  const [locationSearch, setLocationSearch] = useState<string>("");
  const { data: jobPositions, isLoading } = BizJobTitleQueries.useList({
    limit: 50,
    filter: {
      jobTitle: jobPositionSearch,
    },
  });

  const { data: locations, isLoading: isLoadingLocations } = BizLocationQueries.useList({
    limit: 50,
    filter: {
      name: locationSearch,
    },
  });

  const [jobPositionItems, setJobPositionItems] = useState<string[]>([]);
  const [locationItems, setLocationItems] = useState<string[]>([]);

  const handleCreateJobPosition = async (jobPosition: string) => {
    setSelectedJobPosition(jobPosition);
  };

  const addTagToSelectedTags = useCallback((newTag?: CommonModels.BizTagDto) => {
    setSelectedTags((curr) => {
      if (!newTag) {
        return curr;
      }

      if (!curr || curr.length === 0) {
        return [newTag];
      }
      if (curr.find((tag) => tag.id === newTag?.id)) {
        return curr;
      }
      return [...curr, newTag];
    });
  }, []);
  const handleCreateTag = async (tag: string) => {
    const newTag = await createTag({
      data: {
        name: tag,
      },
    });

    addTagToSelectedTags(newTag);

    // Update the cache with the new tag
    const queryKey = BizTagsQueries.keys.listTags(50, undefined, undefined, undefined);
    queryClient.setQueryData(queryKey, (oldData: BizTagsModels.ListTagsResponse | undefined) => {
      if (!oldData?.items) return oldData;

      return {
        ...oldData,
        items: [...oldData.items, newTag],
      };
    });
  };

  const handleSaveAndClose = useCallback(() => {
    onConfirm({
      jobPosition: selectedJobPosition,
      location,
      tags: selectedTags,
    });
  }, [onConfirm, selectedJobPosition, location, selectedTags]);

  useEffect(() => {
    setLocation(metroArea || "");
  }, [metroArea]);

  useEffect(() => {
    setSelectedJobPosition(jobTitle || "");
  }, [jobTitle]);

  useEffect(() => {
    const filteredTags = tagsList?.items?.filter((tag) => tags?.includes(tag.name)) || [];

    // Preserve manually selected tags that aren't in the tags prop
    setSelectedTags((current) => {
      const manuallySelected = current.filter((tag) => !tags?.includes(tag.name));

      // Combine filtered tags from props with manually selected tags
      return [...filteredTags, ...manuallySelected];
    });
  }, [tags, tagsList]);

  useEffect(() => {
    if (isLoading) return;
    const items = jobPositions?.items || [];
    setJobPositionItems(items);

    // Ensure the selected job position is in the items array for proper preselection
    if (selectedJobPosition && !items.includes(selectedJobPosition)) {
      setJobPositionItems([selectedJobPosition, ...items]);
    }
  }, [jobPositions, isLoading, selectedJobPosition]);

  useEffect(() => {
    if (isLoadingLocations) return;
    const items = locations?.items || [];
    setLocationItems(items);

    // Ensure the selected location is in the items array for proper preselection
    if (location && !items.includes(location)) {
      setLocationItems([location, ...items]);
    }
  }, [locations, isLoadingLocations, location]);

  useEffect(() => {
    onChange?.({
      jobPosition: selectedJobPosition,
      location,
      tags: selectedTags,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobPosition, location, selectedTags]);

  return (
    <div className="flex h-full max-h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto pt-8">
        <Typography
          size="body-3"
          className="text-center"
        >
          {subtitle}
        </Typography>
        <div className="flex flex-col gap-5">
          <Autocomplete
            // @ts-ignore
            items={jobPositionItems?.map((position) => ({ id: position, label: position })) || []}
            onSelectionChange={(value) => {
              if (!value) {
                return;
              }
              setSelectedJobPosition(value.toString());
            }}
            selectedKey={selectedJobPosition}
            inputValue={selectedJobPosition}
            label={t("uploads.files-upload-modal.job-title")}
            variant="filled"
            allowsCustomValue={false}
            menuTrigger="focus"
            resetInputAfterSelection={false}
            placeholder={t("uploads.files-upload-modal.select-job-title")}
            onSearchChange={(value) => setJobPositionSearch(value)}
            isLoading={isLoading}
            isRequired
            defaultFilter={() => true}
            onCreateNewOption={(value) => {
              handleCreateJobPosition(value);
            }}
            showNewItemOption={false}
          />
          <Autocomplete
            key="location-select"
            items={locationItems?.map((position) => ({ id: position, label: position })) || []}
            onSelectionChange={(value) => {
              if (!value) {
                return;
              }
              setLocation(value.toString());
            }}
            selectedKey={location}
            inputValue={location}
            label={t("uploads.files-upload-modal.location")}
            variant="filled"
            allowsCustomValue={false}
            menuTrigger="focus"
            resetInputAfterSelection={false}
            placeholder={t("uploads.files-upload-modal.select-location")}
            onSearchChange={(value) => setLocationSearch(value)}
            isLoading={isLoadingLocations}
            isRequired
            defaultFilter={() => true}
            allowsEmptyCollection
          />

          <Autocomplete
            resetInputAfterSelection
            items={tagsList?.items?.map((tag) => ({ id: tag.name, label: tag.name })) || []}
            label={t("uploads.files-upload-modal.tags")}
            onCreateNewOption={(value) => {
              handleCreateTag(value);
            }}
            onSelectionChange={(value) => {
              addTagToSelectedTags(tagsList?.items?.find((tag) => tag.name === value));
            }}
            selectedKey={undefined}
            defaultSelectedKey={undefined}
            allowsCustomValue
            menuTrigger="focus"
            variant="filled"
            placeholder="Select Tags"
            showNewItemOption
          />
          <div className="flex flex-row flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center gap-1-5 rounded-button-rounding bg-elevation-surface-1 px-1-5 py-1"
              >
                <Typography size="body-3">{tag.name}</Typography>
                <IconButton
                  className="!h-6 !bg-elevation-surface-1 !p-0 text-text-default-tertiary"
                  onPress={() => setSelectedTags(selectedTags.filter((tagItem) => tagItem.id !== tag.id))}
                  icon={CloseIcon}
                  iconClassName="h-6 w-6"
                  label=""
                />
              </div>
            ))}
          </div>
        </div>
        {children}
      </div>
      <div className="flex w-full shrink-0 justify-center pt-4">
        <Button
          onPress={handleSaveAndClose}
          variant="outlined"
          width="auto"
          className="!px-4 !py-2 w-[190px] border-gradients-gradient-500 bg-elevation-surface-1 text-interactive-primary-pressed disabled:cursor-not-allowed disabled:bg-elevation-surface-2 disabled:text-interactive-outlined-disabled"
          labelClassName="!text-label-3"
          isDisabled={!selectedJobPosition.trim() || !location.trim()}
        >
          {t("uploads.files-upload-modal.save-and-close")}
        </Button>
      </div>
    </div>
  );
};

export default UploadsProfileForm;
